import { useState, useEffect, FunctionComponent, ReactElement } from 'react';
import { Table, TableRow, TableCell, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import { FieldGroup } from '@lmig/lmds-react-field-group';
import { Heading } from '@lmig/lmds-react-typography';
import { Link } from '@lmig/lmds-react-link';
import { Form } from '@lmig/lmds-react-form';
import { formatPhone, formatEmail } from 'Utilities/StringFormatter';
import states from 'Data/states.json';

const ClaimQuestions: FunctionComponent<IFile> = (props: IFile) => {
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [items, setItems] = useState([] as ICSM[]);
  const [itemCount, setItemCount] = useState(0);
  const [state, setState] = useState('');
  const [lob, setLob] = useState('');

  useEffect(() => {
    const getContacts = async () => {
      if (isSending || !state || !lob) return;
      setItems([]);
      setIsSending(true);
      await fetch(`/api/sitecore/Widget/GetClaimQuestions?id=${props.id}&state=${state}&lob=${lob}`, {
        method: 'GET',
      })
        .then((res) => res.json())
        .then(
          (result) => {
            if (result.length > 0) {
              setItemCount(result.length);
              setItems(result);
            }

            if (result.length === 0) setErrorMessage('No results found');

            setIsSending(false);
          },
          () => {
            setErrorMessage('Something went wrong.');
            setIsSending(false);
          },
        );
    };
    getContacts();
  }, [state, lob]); // eslint-disable-line react-hooks/exhaustive-deps

  const onStateSelected = (event: React.FormEvent<HTMLSelectElement>) => {
    let selected: string = event.currentTarget.value;
    if (selected !== state) setState(selected);
  };

  const onLOBSelected = (event: React.FormEvent<HTMLSelectElement>) => {
    let selected: string = event.currentTarget.value;
    if (selected !== lob) setLob(selected);
  };

  const getCSMContactInfo = (email: string | undefined, phone: string | undefined): ReactElement | string => {
    if (email)
      return (
        <Link variant="navigational" href={`mailto:${formatEmail(email)}`}>
          {formatEmail(email)}
        </Link>
      );
    else if (phone) return formatPhone(phone);
    return '';
  };

  return (
    <>
      <Form style={{ margin: '1rem 0 2rem 0' }}>
        <FieldGroup messages={errorMessage && errorMessage.length > 0 ? [{ text: errorMessage, type: 'error' }] : []}>
          <Select
            inputProps={{ role: 'listbox' }}
            isRequired
            labelA11y="State"
            labelVisual="State"
            placeholderOption="State"
            onChange={(e) => onStateSelected(e)}
            className="claims-select"
          >
            {states.map((state) => (
              <SelectOption key={state.Name} value={state.Name}>
                {state.Name}
              </SelectOption>
            ))}
          </Select>
          <Select
            inputProps={{ role: 'listbox' }}
            isRequired
            labelA11y="Line of business"
            labelVisual="Line of business"
            placeholderOption="Line of business"
            onChange={(e) => onLOBSelected(e)}
            className="claims-select"
          >
            <SelectOption value="Auto">Auto</SelectOption>
            <SelectOption value="Home">Home</SelectOption>
            <SelectOption value="Watercraft">Watercraft</SelectOption>
          </Select>
        </FieldGroup>
      </Form>
      {isSending ? (
        <div style={{ width: '453px', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/-/media/Project/Safeco/shared/Rewards/spinner.gif"
            alt="loading"
            className="loading-spinner"
            style={{ margin: '3rem' }}
          />
        </div>
      ) : (
        <></>
      )}
      {items.length > 0 ? (
        <>
          <Heading align="left" type="h4-light">{`${itemCount} result${itemCount === 1 ? '' : 's'} found`}</Heading>
          <Table style={{ marginBottom: '3.5rem' }}>
            <TableHeader>
              <TableRow>
                <TableCell type="colHead">Claim Question</TableCell>
                <TableCell type="colHead">CSM Name</TableCell>
                <TableCell type="colHead">CSM Contact Information</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.ClaimQuestion}</TableCell>
                  <TableCell>{item.CSMName}</TableCell>
                  <TableCell>{getCSMContactInfo(item.CSMEmail, item.CSMPhone)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default ClaimQuestions;
